import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { X, Star, Send, CheckCircle } from 'lucide-react';

const ContextualFeedback = ({ 
  isOpen, 
  onClose, 
  actionName, 
  actionContext = '', 
  position = 'center',
  autoClose = true 
}) => {
  const { session } = useAuth();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleRatingClick = (value) => {
    setRating(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) return;

    setSubmitting(true);

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'contextual',
          rating,
          message: comment,
          title: `Feedback: ${actionName}`,
          pageUrl: window.location.href,
          actionContext: actionContext,
          actionName: actionName
        })
      });

      if (response.ok) {
        setSubmitted(true);
        if (autoClose) {
          setTimeout(() => {
            onClose();
            resetForm();
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error submitting contextual feedback:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setRating(0);
    setComment('');
    setSubmitted(false);
    setSubmitting(false);
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  if (!isOpen) return null;

  // Position classes
  const positionClasses = {
    center: 'fixed inset-0 flex items-center justify-center z-50',
    'bottom-right': 'fixed bottom-6 right-6 z-50',
    'top-right': 'fixed top-24 right-6 z-50',
    sidebar: 'fixed right-6 top-1/2 transform -translate-y-1/2 z-50'
  };

  const containerClasses = {
    center: 'bg-white rounded-2xl shadow-xl border border-gray-200 p-6 max-w-md w-full mx-4',
    'bottom-right': 'bg-white rounded-2xl shadow-xl border border-gray-200 p-6 w-80',
    'top-right': 'bg-white rounded-2xl shadow-xl border border-gray-200 p-6 w-80',
    sidebar: 'bg-white rounded-2xl shadow-xl border border-gray-200 p-6 w-80'
  };

  return (
    <>
      {/* Backdrop for center position */}
      {position === 'center' && (
        <div className="fixed inset-0 bg-black bg-opacity-25 z-40" onClick={handleClose} />
      )}
      
      <div className={positionClasses[position]}>
        <div className={containerClasses[position]}>
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Just used {actionName}?
              </h3>
              <p className="text-sm text-gray-600">
                How did this feature work for you?
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {submitted ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-green-800 font-medium">Thanks for your feedback!</p>
              <p className="text-sm text-gray-600 mt-1">This helps us improve Banyan</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Rating Scale */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rate your experience (1-5)
                </label>
                <div className="flex space-x-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleRatingClick(value)}
                      className={`w-10 h-10 rounded-full border-2 flex items-center justify-center font-semibold text-sm transition-all ${
                        rating >= value
                          ? 'border-celadon-500 bg-celadon-500 text-white'
                          : 'border-gray-300 text-gray-500 hover:border-celadon-300'
                      }`}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Poor</span>
                  <span>Excellent</span>
                </div>
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tell us more (optional)
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-celadon-500 focus:border-celadon-500 resize-none text-sm"
                  placeholder="What went well? What could be improved?"
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Skip
                </button>
                <button
                  type="submit"
                  disabled={!rating || submitting}
                  className="px-4 py-2 bg-celadon-600 hover:bg-celadon-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span>Send Feedback</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ContextualFeedback; 